"use client";

import { Alert, Stack, Text } from "@chakra-ui/react";
import type { TranscribeAudioMetadata } from "@/generated/fastapi";
import AudioFilePicker from "./AudioFilePicker";
import AudioRecorderPanel from "./AudioRecorderPanel";
import type { RecordedAudioPayload } from "../types";

export type AudioTranscriptInputMode = "voice" | "audioFile";

type AudioTranscriptInputFlowProps = {
  canTranscribeAndSubmit?: boolean;
  errorMessage?: string | null;
  isError?: boolean;
  isTranscribing: boolean;
  mode: AudioTranscriptInputMode;
  readyMessage: string;
  transcribeAndSubmitDisabledReason?: string;
  transcribeAndSubmitLabel?: string;
  transcribeAndSubmitLoadingText?: string;
  transcriptValue: string;
  transcriptionMeta?: TranscribeAudioMetadata | null;
  onReset: () => void;
  onTranscribe: (selectedFile: File) => Promise<void> | void;
  onTranscribeAndSubmit?: (selectedFile: File) => Promise<void> | void;
};

function TranscriptionMetaSummary({
  transcriptionMeta,
}: {
  transcriptionMeta: TranscribeAudioMetadata;
}) {
  const roundedDuration =
    Math.round(transcriptionMeta.duration_seconds * 10) / 10;

  return (
    <>
      Transcript ready from {transcriptionMeta.file_extension} audio,{" "}
      {roundedDuration}s long.
    </>
  );
}

export default function AudioTranscriptInputFlow({
  canTranscribeAndSubmit,
  errorMessage,
  isError = false,
  isTranscribing,
  mode,
  readyMessage,
  transcribeAndSubmitDisabledReason,
  transcribeAndSubmitLabel,
  transcribeAndSubmitLoadingText,
  transcriptValue,
  transcriptionMeta,
  onReset,
  onTranscribe,
  onTranscribeAndSubmit,
}: AudioTranscriptInputFlowProps) {
  const handleRecordingTranscribe = (recording: RecordedAudioPayload) =>
    onTranscribe(recording.file);
  const handleRecordingTranscribeAndSubmit = onTranscribeAndSubmit
    ? (recording: RecordedAudioPayload) => onTranscribeAndSubmit(recording.file)
    : undefined;

  return (
    <Stack gap="4" width="full">
      {mode === "voice" ? (
        <AudioRecorderPanel
          canTranscribeAndSubmit={canTranscribeAndSubmit}
          isTranscribing={isTranscribing}
          transcribeAndSubmitDisabledReason={
            transcribeAndSubmitDisabledReason
          }
          transcribeAndSubmitLabel={transcribeAndSubmitLabel}
          transcribeAndSubmitLoadingText={transcribeAndSubmitLoadingText}
          onReset={onReset}
          onTranscribe={handleRecordingTranscribe}
          onTranscribeAndSubmit={handleRecordingTranscribeAndSubmit}
        />
      ) : (
        <AudioFilePicker
          canTranscribeAndSubmit={canTranscribeAndSubmit}
          isTranscribing={isTranscribing}
          transcribeAndSubmitDisabledReason={
            transcribeAndSubmitDisabledReason
          }
          transcribeAndSubmitLabel={transcribeAndSubmitLabel}
          transcribeAndSubmitLoadingText={transcribeAndSubmitLoadingText}
          onReset={onReset}
          onTranscribe={onTranscribe}
          onTranscribeAndSubmit={onTranscribeAndSubmit}
        />
      )}

      {isError && (
        <Alert.Root status="error" borderRadius="lg">
          <Alert.Indicator />
          <Alert.Description>
            {errorMessage ?? "Failed to transcribe audio."}
          </Alert.Description>
        </Alert.Root>
      )}

      {transcriptionMeta && (
        <Alert.Root status="info" borderRadius="lg">
          <Alert.Indicator />
          <Alert.Description>
            <TranscriptionMetaSummary transcriptionMeta={transcriptionMeta} />
          </Alert.Description>
        </Alert.Root>
      )}

      {transcriptValue && (
        <Text fontSize="sm" color="moveon.text.muted">
          {readyMessage}
        </Text>
      )}
    </Stack>
  );
}
